import type { MiCita, VistaPreviaMiCancelacion } from '../api/ops/citasCliente';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { Ventana } from '../componentes/Ventana';
import { dinero } from '../logica/dinero';
import { navegador } from '../logica/navegador';
import { chipsDeCita, diaLargo, diaYNumero, duracion, horaEn, mayuscula, minutosDe, momentoEn, nombreServicios, profesionalDe, viva } from './citas';
import { BotonPc } from './Pagina';
import './citas.css';

/**
 * La cita vista por el cliente (9.10): la de «Mis citas» (`getMyAppointment`) y la del enlace del mensaje
 * (`getAppointmentByLink`), con sus etiquetas, el código, el dinero que da la API, la firma y lo que puede hacer ahora
 * (`can_modify`, `can_cancel`). Y la ventana de cancelar con el importe antes de confirmar (D-004).
 */
export interface Banda {
    titulo: string;
    texto: string;
}

interface PropsDetalle {
    cita: MiCita;
    /** Abierta desde el enlace del mensaje: para cambiarla hace falta el código; firmar va por su propio enlace. */
    porEnlace: boolean;
    negocio: string;
    banda: Banda | null;
    onCambiar: () => void;
    onCancelar: () => void;
    onFirmar: () => void;
    /** «Pedir otra igual» (pasadas): solo si se sabe la web del negocio. */
    onPedirOtra: (() => void) | null;
    /** «Pagar» (G867): a la página de pago de `payment_action`. */
    onPagar?: (url: string) => void;
}

export function DetalleCita({ cita: c, porEnlace, negocio, banda, onCambiar, onCancelar, onFirmar, onPedirOtra, onPagar = (url) => { navegador.ir(url); } }: PropsDetalle) {
    const vive = viva(c);
    const fuera = vive && !c.can_modify;
    const pro = profesionalDe(c);
    const minutos = minutosDe(c);
    const gratis = c.free_cancellation_until ?? null;
    const devuelto = c.payment_status === 'refunded' || c.payment_status === 'partially_refunded';
    const filas: { l: string; v: string }[] = [{ l: 'Código', v: c.code }, { l: 'Total', v: dinero(c.total_price) }];
    if (c.deposit !== undefined && c.deposit.amount > 0) {
        if (c.status === 'pending_payment') filas.push({ l: 'Señal', v: `${dinero(c.deposit)}, sin pagar` });
        else if (c.status === 'cancelled') filas.push({ l: 'Señal', v: `${dinero(c.deposit)}, ${devuelto ? 'devuelta' : 'no devuelta'}` });
        else filas.push({ l: 'Señal pagada', v: dinero(c.deposit) });
    }
    if (vive) filas.push({ l: 'Cambiar o cancelar gratis', v: fuera || gratis === null ? 'Ya no' : `Hasta el ${momentoEn(gratis, c.timezone)}` });

    const cond: { icono: NombreIcono; titulo: string; texto: string } =
        c.status === 'cancelled'
            ? { icono: 'calendar-x', titulo: 'Cancelada', texto: devuelto ? 'Te devolvimos la señal.' : 'Esta cita está cancelada.' }
            : fuera
              ? { icono: 'phone', titulo: 'Ya no se puede cambiar desde aquí', texto: `${c.cancellation_preview?.conditions_text ?? ''} Para cambiarla, llama a ${negocio}.`.trim() }
              : c.status === 'completed'
                ? { icono: 'scale', titulo: 'Si cambias de idea', texto: 'Gracias por venir. Si quieres repetir, pide otra igual.' }
                : c.status === 'no_show'
                  ? { icono: 'scale', titulo: 'Si cambias de idea', texto: 'No viniste y no la cancelaste.' }
                  : c.status === 'expired'
                    ? { icono: 'scale', titulo: 'Si cambias de idea', texto: 'No se pagó la señal a tiempo y la hora se liberó. No se cobró nada.' }
                    : {
                          icono: 'scale',
                          titulo: 'Si cambias de idea',
                          texto: gratis === null ? (c.cancellation_preview?.conditions_text ?? '') : `Puedes cambiarla o cancelarla gratis hasta el ${momentoEn(gratis, c.timezone)}. ${c.cancellation_preview?.conditions_text ?? ''}`.trim(),
                      };

    return (
        <>
            {banda !== null && (
                <div className="rv-ct-banda" role="status">
                    <Icono nombre="circle-check" />
                    <span><b>{banda.titulo}</b><span>{banda.texto}</span></span>
                </div>
            )}
            <section className="rv-pc-card" aria-labelledby="rv-ct-cita-t">
                {chipsDeCita(c).length > 0 && (
                    <div className="rv-ct-chips">
                        {chipsDeCita(c).map((x) => <Chip key={x.texto} estado={x.chip}>{x.texto}</Chip>)}
                    </div>
                )}
                <div className="rv-pc-card__cab">
                    <h1 id="rv-ct-cita-t">{nombreServicios(c)}</h1>
                    <span className="rv-ct-resumen__linea"><Icono nombre="calendar" /><span>{`${mayuscula(diaLargo(c.local_date))} · ${c.local_time}`}</span></span>
                    <span className="rv-ct-resumen__linea"><Icono nombre="user" /><span>{`${pro === null ? '' : `Con ${pro} · `}${duracion(minutos)}`}</span></span>
                    {c.business_name !== undefined && <span className="rv-ct-resumen__linea"><Icono nombre="map-pin" /><span>{c.business_name}</span></span>}
                </div>
                <div className="rv-ct-filas">
                    {filas.map((f) => <div key={f.l}><span>{f.l}</span><span>{f.v}</span></div>)}
                </div>
                {vive && (c.consent_status === 'requested' || c.consent_status === 'pending') && (
                    <div className="rv-ct-vacio">
                        <span>
                            <Icono nombre="signature" />
                            <span>
                                <b>{c.consent_status === 'pending' ? 'Falta firmar el consentimiento' : 'Tienes un consentimiento por firmar'}</b>
                                <span>
                                    {c.consent_status === 'pending'
                                        ? `Se pasó el plazo para firmarlo desde el móvil. Fírmalo en ${negocio} al llegar; la cita sigue en pie.`
                                        : porEnlace
                                          ? 'Te mandamos el enlace en otro mensaje. Fírmalo antes de la cita, o al llegar.'
                                          : 'Fírmalo desde aquí antes de la cita. Si no te da tiempo, lo firmas al llegar.'}
                                </span>
                            </span>
                        </span>
                        {c.consent_status === 'requested' && !porEnlace && (
                            <div className="rv-ct-alternativas">
                                <button type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" onClick={onFirmar}><Icono nombre="pen-line" /><span>Leer y firmar</span></button>
                            </div>
                        )}
                    </div>
                )}
                {c.status === 'pending_payment' && (
                    <div className="rv-ct-aviso">
                        <Icono nombre="hourglass" />
                        <span>
                            <b>Falta pagar la señal</b>
                            <span>
                                {c.payment_action == null
                                    ? 'Págala con el enlace de pago que te mandamos. Si no la pagas a tiempo, la hora se libera y no se cobra nada.'
                                    : `Te guardamos la hora hasta las ${horaEn(c.payment_action.expires_at, c.timezone)}. Si no la pagas antes, se libera y no se cobra nada.`}
                            </span>
                        </span>
                    </div>
                )}
                {/* G867: el pago pendiente vivo con su enlace (`payment_action`). */}
                {vive && c.payment_action != null && <BotonPc texto={`Pagar ${dinero(c.payment_action.amount)}`} icono="lock" onClick={() => { onPagar(c.payment_action?.url ?? ''); }} />}
                {cond.texto !== '' && (
                    <div className="rv-ct-aviso">
                        <Icono nombre={cond.icono} />
                        <span><b>{cond.titulo}</b><span>{cond.texto}</span></span>
                    </div>
                )}
                {(vive && (c.can_modify || c.can_cancel)) || (!vive && onPedirOtra !== null && !porEnlace) ? (
                    <div className="rv-pc-acciones">
                        {vive && c.can_modify && <BotonPc tipo="sec" texto="Cambiar la cita" icono="calendar-clock" onClick={onCambiar} />}
                        {vive && c.can_cancel && <BotonPc tipo="sec" rojo texto="Cancelar la cita" icono="calendar-x" onClick={onCancelar} />}
                        {!vive && onPedirOtra !== null && !porEnlace && <BotonPc tipo="sec" texto="Pedir otra igual" icono="calendar-plus" onClick={onPedirOtra} />}
                    </div>
                ) : null}
                {porEnlace && vive && (
                    <p className="rv-pc-nota"><Icono nombre="info" /><span>Para cambiarla te mandamos un código, como en «Mis citas». Para cancelarla basta este enlace.</span></p>
                )}
            </section>
        </>
    );
}

interface PropsCancelar {
    abierta: boolean;
    cita: MiCita;
    /** `null` mientras se lee. */
    vista: VistaPreviaMiCancelacion | null;
    cambiada: boolean;
    error: string | null;
    nota: string;
    onNota: (v: string) => void;
    ocupado: boolean;
    onCancelar: () => void;
    onCerrar: () => void;
    movil?: boolean;
}

export function VentanaCancelar({ abierta, cita: c, vista, cambiada, error, nota, onNota, ocupado, onCancelar, onCerrar, movil = false }: PropsCancelar) {
    const filas = vista === null ? [] : [{ l: 'Te devolvemos', v: dinero(vista.refund) }, { l: 'Se queda el negocio', v: dinero(vista.retained) }, ...(vista.late_cancellation_fee !== undefined && vista.late_cancellation_fee.amount > 0 ? [{ l: 'Se cobra', v: dinero(vista.late_cancellation_fee) }] : [])];
    return (
        <Ventana
            abierta={abierta}
            titulo={`¿Cancelar tu cita del ${diaYNumero(c.local_date)}?`}
            texto={vista?.conditions_text ?? ''}
            accion="Cancelar la cita"
            accionIcono="calendar-x"
            cancelar="No cancelar"
            ocupado={ocupado}
            ocupadoTexto="Cancelando…"
            inactiva={vista === null}
            movil={movil}
            barraInferior={false}
            onAccion={onCancelar}
            onCerrar={onCerrar}
        >
            <div className="rv-pc-campos">
                {cambiada && (
                    <div role="alert" className="rv-pc-aviso rv-pc-aviso--espera"><Icono nombre="clock" /><span>Ha pasado la hora límite mientras lo mirabas. Este es el importe de ahora.</span></div>
                )}
                {vista === null ? (
                    <div className="rv-pc-esqueleto" style={{ height: 48 }} />
                ) : (
                    <div className="rv-ct-filas">
                        {filas.map((f) => <div key={f.l}><span>{f.l}</span><span>{f.v}</span></div>)}
                    </div>
                )}
                <label className="rv-pc-campos">
                    <span style={{ fontSize: 14, fontWeight: 700 }}>¿Quieres decirles por qué? (opcional)</span>
                    <textarea className="rv-ct-texto" maxLength={500} rows={2} value={nota} onChange={(e) => { onNota(e.target.value); }} />
                </label>
                {error !== null && <div role="alert" className="rv-pc-aviso"><Icono nombre="circle-alert" /><span>{error}</span></div>}
            </div>
        </Ventana>
    );
}

